import * as SplashScreen from 'expo-splash-screen'
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { Platform } from 'react-native'
import { useTranslation } from 'react-i18next'

import useConstants, { UseConstantsReturnType } from '@ambire-common-v1/hooks/useConstants'
import Satellite from '@common/assets/svg/Satellite'
import Button from '@common/components/Button'
import ScrollableWrapper from '@common/components/ScrollableWrapper'
import Spinner from '@common/components/Spinner'
import Text from '@common/components/Text'
import Title from '@common/components/Title'
import CONFIG from '@common/config/env'
import spacings from '@common/styles/spacings'
import flexboxStyles from '@common/styles/utils/flexbox'
import textStyles from '@common/styles/utils/text'

const ConstantsContext = createContext<{
  constants: UseConstantsReturnType['constants']
}>({
  constants: null
})

const ConstantsProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { t } = useTranslation()
  // useConstants fetches `${endpoint}/result.json` unconditionally. On targets
  // without CONSTANTS_ENDPOINT (mobile-web), that builds `undefined/result.json`
  // and hits the dev server, which returns the index.html — JSON.parse then
  // throws "Unexpected token '<', '<!doctype'". Stub the fetch to skip the
  // request entirely; we already gate the ErrorView below on the same condition.
  const constantsEndpoint = (CONFIG as any).CONSTANTS_ENDPOINT
  // Stub returns a 200 with JSON `null`. fetchCaught resolves cleanly with
  // `{ body: null }`; useConstants then throws "Failed to get the constants"
  // internally and flips hasError once, no console spam.
  const constantsFetch = (constantsEndpoint
    ? fetch
    : (() =>
        Promise.resolve(
          new Response('null', { status: 200, headers: { 'content-type': 'application/json' } })
        ))) as any
  const { constants, isLoading, retryFetch, hasError }: UseConstantsReturnType = useConstants({
    fetch: constantsFetch,
    endpoint: constantsEndpoint || ''
  })
  const [isRetrying, setIsRetrying] = useState<boolean>(false)

  useEffect(() => {
    if (hasError) {
      SplashScreen.hideAsync()
    }
  }, [hasError])

  const retry = useCallback(async () => {
    setIsRetrying(true)
    await retryFetch()

    setIsRetrying(false)
  }, [retryFetch])

  const ErrorView = useMemo(
    () => (
      <ScrollableWrapper contentContainerStyle={flexboxStyles.center}>
        <Satellite style={spacings.mbLg} />
        <Title style={textStyles.center}>{t("Can't connect to our server")}</Title>
        <Text style={[spacings.mb, spacings.mhTy, textStyles.center]}>
          {t('Something went wrong, but your funds are safe! Please try again later.')}
        </Text>
        <Button
          text={isRetrying ? t('Retrying...') : t('Retry')}
          disabled={isRetrying}
          onPress={retry}
        />
      </ScrollableWrapper>
    ),
    [t, isRetrying, retry]
  )

  const render = useCallback(() => {
    // In the mobile app context - there is no need for another (custom)
    // loading view, because the splash screen will always be present
    // while this provider is loading (so it can be `null`)
    // In the web extension context - a (custom) loading view is needed,
    // because there is no splash screen present there. So keep it.
    const LoadingView = (
      <ScrollableWrapper contentContainerStyle={flexboxStyles.center}>
        <Spinner />
      </ScrollableWrapper>
    )

    if (isLoading) {
      return LoadingView
    }

    // CONSTANTS_ENDPOINT is web-extension-only (legacy v1 hook). Skip the Retry
    // view on any target where the endpoint is not configured — native (RN) and
    // mobile-as-webapp both fall through so the app reaches auth/onboarding.
    if (Platform.OS !== 'web' || !(CONFIG as any).CONSTANTS_ENDPOINT) return children

    return hasError ? ErrorView : children
  }, [isLoading, hasError, ErrorView, children])

  return (
    <ConstantsContext.Provider value={useMemo(() => ({ constants }), [constants])}>
      {render()}
    </ConstantsContext.Provider>
  )
}

export { ConstantsContext, ConstantsProvider }
